<template>
  <div class="flex flex-col bg-white space-y-2 lg:space-y-0 lg:flex-row justify-between items-center border border-solid border-gray-200 rounded-lg px-1.5 py-1.5">
    <!-- ESQUERDA -->
    <div class="flex items-center space-x-1 flex-wrap md:flex-nowrap overflow-x-auto md:overflow-visible">
      <button
        v-if="isDiscussionLive"
        @click="$emit('toggleFullscreen')"
        class="border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="Fullscreen"
        title="Fullscreen"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-expand" />
          <span class="hidden md:inline">Fullscreen</span>
        </div>
      </button>

      <button 
        v-if="!isSecondScreenOpen && isDiscussionLive && !isDiscussionSharingScreen && !isDiscussionAudioEnabe" 
        @click="handleOpenSecondScreen()" 
        class="sample border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="2nd Screen"
        title="2nd Screen"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <img class="w-4 h-4" src="/static/live_discussion/2nd_screen.svg" />
          <span class="hidden md:inline">2nd Screen</span>
        </div>
      </button>

      <button 
        @click="handleChangeVideoStatus()"
        v-if="isDiscussionLive && isDiscussionAudioEnabe" 
        class="sample border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        :aria-label="cameraOn ? 'Turn camera off' : 'Turn camera on'"
        :title="cameraOn ? 'Turn camera off' : 'Turn camera on'"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i :class="cameraOn === true ? 'fa-solid fa-video' : 'fa-solid fa-video-slash text-red-500'"></i>
        </div>
      </button>

      <button
        v-if="isDiscussionLive && isDiscussionAudioEnabe"  
        @click="handleChangeMicroStatus()" 
        class="sample text-center w-8 md:w-12 h-8 rounded-md px-0 md:px-3.5 py-1 hover:bg-gray-100 text-gray-700 border border-solid border-gray-300"
        :aria-label="micOn ? 'Mute' : 'Unmute'"
        :title="micOn ? 'Mute' : 'Unmute'"
      >
        <div class="flex items-center justify-center md:justify-start space-x-0 md:space-x-2">
          <i :class="micOn === false ? 'fa-solid fa-microphone-slash text-red-500' : 'fa-solid fa-microphone ml-1'"></i>
        </div>
      </button>

      <button 
        v-if="isDiscussionLive && isDiscussionSharingScreen === false && isDiscussionAudioEnabe" 
        @click="handleStartSharingScreen()" 
        class="sample border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="Share Screen"
        title="Share Screen"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <img src="/static/chrome-cast.svg" style="height: 17px; width: 17px">
          <span class="hidden md:inline">Share Screen</span>
        </div>
      </button>

      <button 
        v-if="isDiscussionLive && isDiscussionSharingScreen === true && isDiscussionAudioEnabe" 
        @click="handleStopSharingScreen()" 
        class="sample border border-solid border-gray-300 hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="Stop Sharing"
        title="Stop Sharing"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <img src="/static/stop-screen.svg" style="height: 17px; width: 17px">
          <span class="hidden md:inline">Stop Sharing</span>
        </div>
      </button>
    </div>

    <!-- DIREITA -->
    <div class="flex items-center flex-wrap md:flex-nowrap">
      <button
        @click="handleOpenPolls()"
        class="hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1"
        aria-label="Polls"
        title="Polls"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-chart-pie"></i>
          <span class="hidden md:inline">Polls</span>
        </div>
      </button>

      <button
        @click="handleOpenQuestions()"
        class="hover:bg-gray-100 h-8 text-gray-700 rounded-md px-2 md:px-3.5 py-1 mr-0 md:mr-1"
        aria-label="Questions"
        title="Questions"
      >
        <div class="flex items-center space-x-0 md:space-x-2">
          <i class="fa-solid fa-circle-question"></i>
          <span class="hidden md:inline">Questions</span>
        </div>
      </button>

      <div v-if="isDiscussionLive" class="mr-0 md:mr-1">
        <button 
          v-if="getCurrentDiscussion.teacher_tapped_in_id !== null" 
          @click="handleTeacherTapOut()"  
          class="sample hover:bg-gray-100 border border-solid border-gray-300 text-gray-700 h-8 rounded-md px-2 md:px-3.5 py-1"
          :disabled="!ImTappedInTeacher"
          aria-label="Tap Out"
          title="Tap Out"
        >
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-arrow-up-from-bracket rotate-90" />
            <span class="hidden md:inline">Tap Out</span>
          </div>
        </button>

        <button
          v-else-if="isDiscussionSharingScreen === false"  
          @click="handleTeacherTapIn()" 
          class="sample hover:bg-gray-100 border border-solid border-gray-300 text-gray-700  h-8 rounded-md px-2 md:px-3.5 py-1"
          aria-label="Tap In"
          title="Tap In"
        >
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-arrow-up-from-bracket rotate-90" />
            <span class="hidden md:inline">Tap In</span>
          </div>
        </button>
      </div>

      <div>
        <button 
          v-if="isDiscussionLive" 
          @click="handleEndDiscussion()" 
          class="sample bg-red-500 hover:bg-red-600 h-8 border border-solid border-red-500 text-white rounded-md px-2 md:px-3.5 py-1"
          aria-label="Stop"
          title="Stop"
        >
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-stop"></i>
            <span class="hidden md:inline">Stop</span>
          </div>
        </button>

        <button
          v-else  
          @click="handleRestartLiveDiscussion()" 
          class="bg-green-700 hover:bg-green-800 h-8 border border-solid border-green-700 hover:border-green-800 text-white rounded-md px-2 md:px-3.5 py-1"
          aria-label="Start"
          title="Start"
        >
          <div class="flex items-center space-x-0 md:space-x-2">
            <i class="fa-solid fa-play -mt-0.5"></i>
            <span class="hidden md:inline" style="margin-top: -1px;">Start</span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';
  
  // API Services
  import { teacherTapIn, teacherTapOut } from 'services/liveDiscussion/circle/circleServices.js';

  export default {
    data() {
      return {
        show_start_discussion: false,
        show_confirm_end_discussion: false,
        is_loading_actions: false,
        cameraOn: true,
        micOn: false,
      };
    },
    props: {
      joinedAudioDiscussion: {
        type: Boolean,
        default: false,
      },
      statusMicro: {
        type: Boolean,
      },
      statusCamera: {
        type: Boolean,
      }
    },
    computed: {
      ...mapGetters(['getCurrentDiscussion', 'getProfile']),
      isDiscussionLive() {
        return this.getCurrentDiscussion.is_live;
      },
      isDiscussionSharingScreen() {
        return this.getCurrentDiscussion.is_screen_sharing_active;
      },
      isDiscussionAudioEnabe() {
        return this.getCurrentDiscussion.audio_discussion_is_enabled;
      },
      ImTappedInTeacher() {
        return (
          this.getCurrentDiscussion.teacher_tapped_in_id === this.getProfile.id
        );
      },
      isSecondScreenOpen() {
        return this.$route.query.second_screen;
      },
      isSharingScreenActive() {
        return this.getCurrentDiscussion.is_screen_sharing_active;
      }
    },
    methods: {
      handleStopSharingScreen: async function() {
        EventBus.$emit('handleStopShareScreen');
      },
      handleStartSharingScreen: async function() {
        const { isSharingScreenActive } = this;
        if (isSharingScreenActive) return;
        EventBus.$emit('handleStartShareScreen', true);
      },
      handleChangeVideoStatus: async function() {
        this.cameraOn = !this.cameraOn;
        if (this.cameraOn) {
          EventBus.$emit('videoOn');
          EventBus.$emit('isCameraOn', true);
        } else {
          EventBus.$emit('videoOff');
          EventBus.$emit('isCameraOn', false);
        }
      },
      handleChangeMicroStatus: async function() {
        this.micOn = !this.micOn;
        if (this.micOn) {
          EventBus.$emit('unmuteAudio');
        } else {
          EventBus.$emit('muteAudio');
        }
      },
      handleOpenQuestions: async function() {
        EventBus.$emit('handleOpenGuidingQuestions');
      },
      handleOpenPolls: async function() {
        EventBus.$emit('handleOpenPolls');
      },
      handleEndDiscussion: async function() {
        return EventBus.$emit('handleEndDiscussion');
      },
      handleRestartLiveDiscussion: async function() {
        if (this.getCurrentDiscussion.is_ended) {
          await this.$store.dispatch('restartLiveDiscussion', this.getCurrentDiscussion.id);
          EventBus.$emit('restartLD');
        } else {
          if (!this.getCurrentDiscussion.participations.length) {
            const toast = {
              type: 'Info',
              message: 'You need to invite students before starting the discussion.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            return;
          }
          EventBus.$emit('handleOpenStartDiscussionModal');
        }
      },
      handleOpenSecondScreen: async function() {
        const { courseID, liveDiscussionID } = this.$route.params;
        const route = this.$router.resolve({
          name: 'live-discussion.discussion',
          params: { courseID, liveDiscussionID },
          query: { second_screen: true },
        });
        window.open(route.href, '_blank');
      },
      handleTeacherTapIn() {
        teacherTapIn(this.getCurrentDiscussion.id, 'challenge');
      },
      handleTeacherTapOut() {
        teacherTapOut(this.getCurrentDiscussion.id);
      },
    },
    mounted() {
      EventBus.$on('startDiscussion', () => {
        this.handleRestartLiveDiscussion();
      });
    },
    beforeDestroy() {
      EventBus.$off('startDiscussion');
    },
  };
</script>



// WEBPACK FOOTER //
// LDDiscussionTeacherBar.vue?42c95ab0